(function () {
  'use strict';

  const Api = {
    base: '',
    customerId: '',
    init(base, customerId) {
      this.base = (base || '').replace(/\/$/, '');
      this.customerId = customerId || '';
    },
    _headers(json) {
      const h = { 'X-Customer-Id': this.customerId, 'ngrok-skip-browser-warning': 'true' };
      if (json) h['Content-Type'] = 'application/json';
      return h;
    },
    async _parse(res) {
      const text = await res.text();
      let data = {};
      try { data = text ? JSON.parse(text) : {}; } catch (e) { data = {}; }
      if (!res.ok) throw new Error((data && data.message) || ('HTTP ' + res.status));
      return data;
    },
    session(profile) {
      return fetch(this.base + '/api/auth/session', {
        method: 'POST', headers: this._headers(true), body: JSON.stringify(profile),
      }).then((r) => this._parse(r));
    },
    dashboard() {
      return fetch(this.base + '/api/progress/dashboard', {
        method: 'GET', headers: this._headers(false),
      }).then((r) => this._parse(r));
    },
    watch(levelId, courseId, lectureId, watchPercent) {
      return fetch(this.base + '/api/progress/watch', {
        method: 'PATCH', headers: this._headers(true),
        body: JSON.stringify({ levelId, courseId, lectureId, watchPercent }),
      }).then((r) => this._parse(r));
    },
    submitQuiz(courseId, answers) {
      return fetch(this.base + '/api/quiz/submit', {
        method: 'POST', headers: this._headers(true),
        body: JSON.stringify({ courseId, answers }),
      }).then((r) => this._parse(r));
    },
    quizResult(courseId) {
      return fetch(this.base + '/api/quiz/result?courseId=' + encodeURIComponent(courseId), {
        method: 'GET', headers: this._headers(false),
      }).then((r) => this._parse(r));
    },
    /* Notify the backend that a level is complete so IT can credit HyCash
       (the reward API key stays server-side). */
    levelComplete(level, shopifyCustomerId, levelId) {
      return fetch(this.base + '/api/progress/level-complete', {
        method: 'POST', headers: this._headers(true),
        body: JSON.stringify({ level: level, shopify_customer_id: shopifyCustomerId, levelId: levelId }),
      }).then((r) => this._parse(r));
    },
    subscribeNotification(subscribe) {
      return fetch(this.base + '/api/notifications/subscribe', {
        method: 'POST', headers: this._headers(true),
        body: JSON.stringify({ subscribe: !!subscribe }),
      }).then((r) => this._parse(r));
    },
  };

  const CT = {
    push(name, props) {
      try {
        const ct = window.clevertap;
        if (ct && ct.event && typeof ct.event.push === 'function') {
          ct.event.push(name, props || {});
          console.log('[Academy CT] ✓ ' + name, props); // TODO: remove after testing
        } else {
          console.warn('[Academy CT] ✗ (SDK not loaded) ' + name, props); // TODO: remove after testing
        }
      } catch (e) {
        console.warn('[Academy CT] ✗ (error) ' + name, e); // TODO: remove after testing
      }
    },
  };

  let _ytApiPromise = null;
  function loadYouTubeApi() {
    if (_ytApiPromise) return _ytApiPromise;
    _ytApiPromise = new Promise((resolve) => {
      try {
        if (window.YT && window.YT.Player) { resolve(window.YT); return; }
        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = function () {
          if (typeof prev === 'function') { try { prev(); } catch (e) {} }
          resolve(window.YT);
        };
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        tag.onerror = () => resolve(null);
        document.head.appendChild(tag);
      } catch (e) { resolve(null); }
    });
    return _ytApiPromise;
  }

  class AcademyToast extends HTMLElement {
    show(msg) {
      this.textContent = msg;
      this.classList.add('is-visible');
      clearTimeout(this._t);
      this._t = setTimeout(() => this.classList.remove('is-visible'), 2600);
    }
  }

  class AcademyLecturePlayer extends HTMLElement {
    connectedCallback() {
      this._loaded = false;
      this._watchSent = false;
      this._playWatchSent = false;
      this._initialHTML = this.innerHTML;
      this.addEventListener('click', () => this._play());

      this._reel = !!this._instagramCode() && !this._youtubeId();
      this.classList.toggle('lecture-video--reel', this._reel);

      if (this._reel) {
        this.querySelector('.lecture-video__play')?.setAttribute('hidden', '');
        this.querySelector('.lecture-video__label')?.setAttribute('hidden', '');
        this._observeAndLoad();
        return;
      }

      this._mq = window.matchMedia('(min-width: 768px)');
      this._applyPoster();
      const onChange = () => this._applyPoster();
      if (this._mq.addEventListener) this._mq.addEventListener('change', onChange);
      else if (this._mq.addListener) this._mq.addListener(onChange);
    }

    disconnectedCallback() { this._io?.disconnect(); if (this._ytPoll) { clearInterval(this._ytPoll); this._ytPoll = null; } }

    pause() {
      try {
        if (this._video && typeof this._video.pause === 'function') { this._video.pause(); return; }
        if (this._ytPlayer && typeof this._ytPlayer.pauseVideo === 'function') { this._ytPlayer.pauseVideo(); this._stopYtPoll(); return; }
        if (this._loaded) this._resetPlayer();
      } catch (e) { /* best-effort */ }
    }

    _resetPlayer() {
      this._stopYtPoll();
      this._ytPlayer = null;
      this._video = null;
      this._loaded = false;
      this._playWatchSent = false;
      if (this._initialHTML != null) this.innerHTML = this._initialHTML;
      if (this._reel) this._observeAndLoad();
    }

    _app() { return this.closest('academy-app'); }

    _videoMeta() {
      const yt = this._youtubeId();
      const ig = this._instagramCode();
      const file = this.dataset.videoUrl;
      const screen = this.closest('[data-screen^="screen-lecture-"]');
      const lectureId = screen && screen.dataset.lectureId;
      const courseId = screen && screen.dataset.courseId;
      const app = this._app();
      let mediaType = 'Upload', videoId = '', videoUrl = '';
      if (yt) { mediaType = 'YouTube'; videoId = yt; videoUrl = this.dataset.youtubeUrl || ''; }
      else if (ig) { mediaType = 'Instagram'; videoId = ig.code; videoUrl = this.dataset.instagramUrl || ''; }
      else if (file) { mediaType = 'Upload'; videoId = (String(file).split('/').pop() || '').split('?')[0]; videoUrl = file; }
      const lectureName = (app && app.cat && app.cat.lecture[lectureId]) ? app.cat.lecture[lectureId].name : '';
      const hier = (app && app._hier) ? app._hier({ lectureId: lectureId, courseId: courseId }) : {};
      return { mediaType, videoId, videoUrl, lectureId, courseId, lectureName, hier };
    }

    _vtrack(name, extra) {
      const app = this._app();
      if (!app || !app._track) return;
      const m = this._meta || this._videoMeta();
      const base = Object.assign({
        'Media Type': m.mediaType, 'Video ID': m.videoId,
        'Video Name': m.lectureName, 'Video URL': m.videoUrl,
      }, m.hier);
      app._track(name, Object.assign(base, extra || {}));
    }

    _reportWatched(percent) {
      if (this._watchSent) return;
      this._watchSent = true;
      this.dispatchEvent(new CustomEvent('academy:lecture-watched', {
        bubbles: true, detail: { percent: Math.round(percent) },
      }));
    }

    _reportPlay(percent) {
      if (this._playWatchSent) return;
      this._playWatchSent = true;
      this.dispatchEvent(new CustomEvent('academy:lecture-played', {
        bubbles: true, detail: { percent: Math.round(percent || 0) },
      }));
    }

    _progressTick(currentTime, duration) {
      if (!duration || duration <= 0) return;
      if (this._lastT != null) {
        const dt = currentTime - this._lastT;
        if (dt > 0 && dt < 2) this._watched += dt;
      }
      this._lastT = currentTime;
      const pct = Math.min(100, (currentTime / duration) * 100);
      [25, 50, 75, 100].forEach((mls) => {
        if (pct >= mls && !this._pctHits[mls]) {
          this._pctHits[mls] = true;
          this._vtrack('Academy Video Progress', {
            'Watch Percentage': mls,
            'Video Position (s)': Math.round(currentTime),
            'Video Duration (s)': Math.round(duration),
            'Watch Duration (s)': Math.round(this._watched),
          });
        }
      });
    }

    _emitPlay() {
      const currentPct = (this._video && this._video.duration)
        ? Math.round((this._video.currentTime / this._video.duration) * 100)
        : (this._ytPlayer && this._ytPlayer.getDuration && this._ytPlayer.getDuration())
          ? Math.round(((this._ytPlayer.getCurrentTime ? this._ytPlayer.getCurrentTime() : 0) / this._ytPlayer.getDuration()) * 100)
          : 0;
      this._reportPlay(currentPct);

      if (this._ended) {
        this._ended = false;
        this._pctHits = {};
        this._vtrack('Academy Video Rewatched');
        this._vtrack('Academy Video Started', { 'Rewatch': true });
        this._startedFired = true;
        return;
      }
      if (!this._startedFired) {
        this._startedFired = true;
        this._vtrack('Academy Video Started', { 'Rewatch': false });
      } else {
        this._vtrack('Academy Video Resumed', { 'Video Position (s)': Math.round(this._lastT || 0) });
      }
    }

    _emitPaused(t, d) {
      this._vtrack('Academy Video Paused', {
        'Video Position (s)': Math.round(t || 0),
        'Video Duration (s)': Math.round(d || 0),
        'Watch Duration (s)': Math.round(this._watched),
        'Watch Percentage': d ? Math.round((t / d) * 100) : undefined,
      });
    }

    _emitCompleted(d) {
      this._ended = true;
      this._vtrack('Academy Video Completed', {
        'Video Duration (s)': Math.round(d || 0),
        'Watch Duration (s)': Math.round(this._watched),
        'Watch Percentage': 100,
      });
    }

    _emitSeek(from, to) {
      this._vtrack('Academy Video Seeked', {
        'Seek From (s)': Math.round(from || 0),
        'Seek To (s)': Math.round(to || 0),
        'Seek Direction': (to >= from) ? 'Forward' : 'Backward',
      });
    }

    _play() {
      if (this._loaded) return;
      this._meta = this._videoMeta();
      this._pctHits = {};
      this._watched = 0;
      this._lastT = 0;
      this._ended = false;
      this._startedFired = false;

      const m = this._meta;
      if (m.mediaType === 'YouTube') this._playYouTube(m.videoId);
      else if (m.mediaType === 'Instagram') { this._playInstagram(); this._vtrack('Academy Video Started', { 'Playback': 'Embed (no progress tracking)' }); }
      else if (m.videoUrl) this._playFile(m.videoUrl);
      else return;

      this._loaded = true;
      this._reportPlay(0);
    }

    _playInstagram() {
      const ig = this._instagramCode();
      const f = document.createElement('iframe');
      f.src = 'https://www.instagram.com/' + ig.type + '/' + ig.code + '/embed/';
      f.title = 'Instagram reel';
      f.setAttribute('frameborder', '0');
      f.setAttribute('scrolling', 'no');
      f.allow = 'encrypted-media; picture-in-picture; clipboard-write';
      f.allowFullscreen = true;
      this.innerHTML = ''; this.appendChild(f);
    }

    _playFile(file) {
      const v = document.createElement('video');
      v.src = file; v.controls = true; v.autoplay = true; v.preload = 'metadata';
      v.setAttribute('playsinline', '');
      const poster = this._currentPoster(); if (poster) v.poster = poster;
      this._video = v;
      v.addEventListener('timeupdate', () => {
        if (!v.duration) return;
        const pct = (v.currentTime / v.duration) * 100;
        if (pct >= 90) this._reportWatched(pct);
        this._progressTick(v.currentTime, v.duration);
      });
      v.addEventListener('play', () => this._emitPlay());
      v.addEventListener('pause', () => { if (!v.ended) this._emitPaused(v.currentTime, v.duration); });
      v.addEventListener('ended', () => { this._reportWatched(100); this._progressTick(v.duration, v.duration); this._emitCompleted(v.duration); });
      v.addEventListener('seeking', () => { this._seekFrom = this._lastT; });
      v.addEventListener('seeked', () => {
        const to = v.currentTime;
        if (Math.abs(to - (this._seekFrom || 0)) >= 2) this._emitSeek(this._seekFrom, to);
        this._lastT = to;
      });
      this.innerHTML = ''; this.appendChild(v);
    }

    _playYouTube(videoId) {
      const holder = document.createElement('div');
      holder.className = 'lecture-video__yt';
      holder.style.width = '100%'; holder.style.height = '100%';
      this.innerHTML = ''; this.appendChild(holder);
      loadYouTubeApi().then((YT) => {
        if (!YT || !YT.Player) {
          const f = document.createElement('iframe');
          f.src = 'https://www.youtube-nocookie.com/embed/' + videoId + '?autoplay=1&rel=0';
          f.title = 'Lecture video';
          f.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
          f.allowFullscreen = true;
          f.style.width = '100%'; f.style.height = '100%';
          this.innerHTML = ''; this.appendChild(f);
          this._vtrack('Academy Video Started', { 'Playback': 'Iframe fallback (no progress tracking)' });
          return;
        }
        this._ytPlayer = new YT.Player(holder, {
          videoId: videoId,
          host: 'https://www.youtube-nocookie.com',
          playerVars: { autoplay: 1, rel: 0, playsinline: 1 },
          events: { onStateChange: (e) => this._onYtState(e) },
        });
      });
    }

    _onYtState(e) {
      const YT = window.YT;
      if (!YT || !YT.PlayerState) return;
      const p = this._ytPlayer;
      if (e.data === YT.PlayerState.PLAYING) {
        this._emitPlay();
        this._startYtPoll();
      } else if (e.data === YT.PlayerState.PAUSED) {
        this._stopYtPoll();
        this._emitPaused(p.getCurrentTime ? p.getCurrentTime() : 0, p.getDuration ? p.getDuration() : 0);
      } else if (e.data === YT.PlayerState.ENDED) {
        this._stopYtPoll();
        const d = p.getDuration ? p.getDuration() : 0;
        this._progressTick(d, d);
        this._reportWatched(100);
        this._emitCompleted(d);
      }
    }

    _startYtPoll() {
      this._stopYtPoll();
      this._ytPoll = setInterval(() => {
        const p = this._ytPlayer;
        if (!p || !p.getCurrentTime) return;
        const t = p.getCurrentTime();
        const d = p.getDuration ? p.getDuration() : 0;
        const prevT = this._lastT;
        if (prevT != null && Math.abs(t - prevT) >= 2.5) this._emitSeek(prevT, t); // jump = seek/skip
        this._progressTick(t, d);
        if (d && (t / d) * 100 >= 90) this._reportWatched((t / d) * 100);
      }, 1000);
    }

    _stopYtPoll() { if (this._ytPoll) { clearInterval(this._ytPoll); this._ytPoll = null; } }

    _observeAndLoad() {
      if (!('IntersectionObserver' in window)) { this._play(); return; }
      this._io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { this._io.disconnect(); this._play(); }
      });
      this._io.observe(this);
    }

    _currentPoster() {
      const d = this.dataset.posterDesktop, m = this.dataset.posterMobile;
      const chosen = this._mq && this._mq.matches ? (d || m) : (m || d);
      if (chosen) return chosen;
      const yt = this._youtubeId();
      return yt ? 'https://i.ytimg.com/vi/' + yt + '/hqdefault.jpg' : null;
    }

    _applyPoster() {
      if (this._loaded) return;
      const url = this._currentPoster();
      if (url) {
        this.style.backgroundImage = 'url(' + url + ')';
        this.style.backgroundSize = 'cover';
        this.style.backgroundPosition = 'center';
      } else { this.style.backgroundImage = ''; }
    }

    _youtubeId() {
      const url = this.dataset.youtubeUrl;
      if (!url) return null;
      const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
      return m ? m[1] : null;
    }

    _instagramCode() {
      const url = this.dataset.instagramUrl;
      if (!url) return null;
      const m = url.match(/instagram\.com\/(reel|reels|p|tv)\/([A-Za-z0-9_-]+)/);
      if (!m) return null;
      return { type: m[1] === 'reels' ? 'reel' : m[1], code: m[2] };
    }
  }

  class AcademyQuiz extends HTMLElement {
    connectedCallback() {
      this.questions = Array.from(this.querySelectorAll('[data-question]'));
      this.nextBtn = this.querySelector('[data-quiz-next]');
      this.counter = this.querySelector('[data-quiz-counter]');
      this.label = this.querySelector('[data-quiz-label]');
      this.progress = this.querySelector('[data-quiz-progress]');
      this.closeBtn = this.querySelector('[data-quiz-close]');
      this.current = 0;
      this.answers = [];
      this._wired = false;
    }

    get app() { return this.closest('academy-app'); }
    get courseId() { return this.dataset.courseId; }

    _qctx(extra) {
      const app = this.app;
      const cid = this.courseId;
      const base = Object.assign({
        'Quiz ID': this.dataset.quizId || cid,
        'Quiz Name': this.dataset.quizName || '',
        'Total Questions': parseInt(this.dataset.totalQuestions, 10) || this.questions.length,
      }, (app && app._hier) ? app._hier({ courseId: cid }) : {});
      if (extra) Object.keys(extra).forEach((k) => { if (extra[k] !== undefined) base[k] = extra[k]; });
      return base;
    }
    _qtrack(name, extra) { if (this.app && this.app._track) this.app._track(name, this._qctx(extra)); }

    _wire() {
      if (this._wired) return;
      this._wired = true;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-quiz-close]')) {
          this._qtrack('Academy CTA Clicked', { 'CTA Name': 'Close Quiz', 'CTA Type': 'Quiz Nav', 'Screen Name': 'Quiz' });
          this.app.navigate(this.dataset.back); return;
        }
        if (this.classList.contains('academy-quiz--review')) {
          if (e.target.closest('[data-quiz-next]')) {
            this._qtrack('Academy CTA Clicked', { 'CTA Name': 'Back to Course', 'CTA Type': 'Quiz Nav', 'Screen Name': 'Quiz Review' });
            this.app.navigate(this.dataset.back);
          }
          return;
        }
        const opt = e.target.closest('.quiz-option');
        if (opt) { this._select(opt); return; }
        if (e.target.closest('[data-quiz-next]') && !this.nextBtn.disabled) { this._next(); return; }
      });
    }

    async start() {
      this._wire();
      this.classList.remove('academy-quiz--review');
      if (this.app.isCourseQuizSubmitted(this.courseId)) {
        try {
          const data = await Api.quizResult(this.courseId);
          this._review(data.result);
          this._qtrack('Academy Quiz Reviewed', {
            'Correct Count': data.result && data.result.correctCount,
          });
          return;
        } catch (e) { this.app.toast.show('Could not load your results.'); }
      }
      this._fresh();
      this._quizStartedAt = Date.now();
      this._qtrack('Academy Quiz Started');
    }

    _fresh() {
      this.current = 0;
      this.answers = [];
      this.questions.forEach((q, i) => {
        q.hidden = i !== 0;
        q.querySelectorAll('.quiz-option').forEach((o) => o.classList.remove('is-selected', 'is-correct', 'is-wrong', 'is-answered'));
        const ex = q.querySelector('[data-quiz-explanation]'); if (ex) ex.hidden = true;
      });
      this.nextBtn.disabled = true;
      this.nextBtn.textContent = this.questions.length > 1 ? 'Next →' : 'Submit';
      this._sync();
    }

    _select(opt) {
      const q = this.questions[this.current];
      q.querySelectorAll('.quiz-option').forEach((o) => o.classList.remove('is-selected'));
      opt.classList.add('is-selected');
      this.answers[this.current] = {
        questionId: q.dataset.questionId,
        chosenIndex: parseInt(opt.dataset.index, 10),
      };
      this.nextBtn.disabled = false;
      this._qtrack('Academy Quiz Answer Selected', {
        'Question ID': q.dataset.questionId,
        'Question Number': this.current + 1,
        'Chosen Index': parseInt(opt.dataset.index, 10),
      });
    }

    _sync() {
      const total = this.questions.length;
      const n = this.current + 1;
      if (this.counter) this.counter.textContent = 'Q ' + n + '/' + total;
      if (this.label) this.label.textContent = 'Question ' + n + ' of ' + total;
      if (this.progress) this.progress.style.width = Math.round((n / total) * 100) + '%';
      const last = this.current === total - 1;
      this.nextBtn.textContent = last ? 'Submit' : 'Next →';
    }

    async _next() {
      if (this.current < this.questions.length - 1) {
        const from = this.current + 1;
        this.questions[this.current].hidden = true;
        this.current += 1;
        this.questions[this.current].hidden = false;
        this.nextBtn.disabled = !this.answers[this.current];
        this._sync();
        this._qtrack('Academy Quiz Advanced', { 'From Question': from, 'To Question': this.current + 1 });
        return;
      }
      this.nextBtn.disabled = true;
      this.nextBtn.textContent = 'Submitting…';
      try {
        const data = await Api.submitQuiz(this.courseId, this.answers);
        const r = data.result || {};
        const total = this.questions.length;
        this._qtrack('Academy Quiz Completed', {
          'Answered Count': this.answers.filter(Boolean).length,
          'Correct Count': r.correctCount,
          'Score (%)': (total && typeof r.correctCount === 'number') ? Math.round((r.correctCount / total) * 100) : undefined,
          'Stars': r.stars,
          'Grade': r.grade,
          'HYcash Earned': r.hycashEarned,
          'Passed': !!(r.stars && r.stars > 0),
          'Engagement Time (s)': this._quizStartedAt ? Math.round((Date.now() - this._quizStartedAt) / 1000) : undefined,
        });
        await this.app.refreshDashboard();
        this.app.showResult(data.result, this.courseId);
      } catch (e) {
        this.app.toast.show('Could not submit the quiz. Please try again.');
        this.nextBtn.disabled = false;
        this.nextBtn.textContent = 'Submit';
      }
    }

    _review(result) {
      this.classList.add('academy-quiz--review');
      const byId = {};
      (result.answers || []).forEach((a) => { byId[a.questionId] = a; });
      this.questions.forEach((q) => {
        q.hidden = false;
        const a = byId[q.dataset.questionId];
        const opts = q.querySelectorAll('.quiz-option');
        opts.forEach((o) => {
          o.classList.add('is-answered');
          const idx = parseInt(o.dataset.index, 10);
          const mark = o.querySelector('.quiz-option__mark');
          if (a && idx === a.correctIndex) { o.classList.add('is-correct'); if (mark) mark.textContent = '✓'; }
          if (a && idx === a.chosenIndex && !a.isCorrect) { o.classList.add('is-wrong'); if (mark) mark.textContent = '✕'; }
        });
        const ex = q.querySelector('[data-quiz-explanation]'); if (ex) ex.hidden = false;
      });
      if (this.counter) this.counter.textContent = 'Reviewed';
      if (this.label) this.label.textContent = (result.correctCount || 0) + ' / ' + (result.totalQuestions || this.questions.length) + ' correct';
      if (this.progress) this.progress.style.width = '100%';
      this.nextBtn.disabled = false;
      this.nextBtn.textContent = '← Back to course';
    }
  }

  class AcademyApp extends HTMLElement {
    connectedCallback() {
      this.toast = this.querySelector('academy-toast');
      this.loadingEl = this.querySelector('[data-loading]');
      this.gateEl = this.querySelector('[data-gate]');
      this.resultModal = this.querySelector('[data-result-modal]');
      this.certModal = this.querySelector('[data-cert-modal]');

      this._readContext();
      this._calcHeaderOffset();
      this._buildRelations();
      this._bindClicks();
      this._bindWatchEvents();
      this._wireResultModal();
      this._wireCertModal();

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this._pauseCurrentVideo();
      });

      this.history = ['home'];
      this.boot();
    }

    _calcHeaderOffset() {
      const detect = () => {
        // Check if running in App / Webview mode via URL or body/html class
        const urlParams = new URLSearchParams(window.location.search);
        const isApp = urlParams.has('is_app') || urlParams.has('app') || urlParams.has('webview') ||
                      urlParams.has('in_app') ||
                      document.body.classList.contains('is-app') ||
                      document.documentElement.classList.contains('is-app') ||
                      document.body.classList.contains('appbrew-theme');

        if (isApp) {
          this.style.setProperty('--ha-header-height', '0px');
          return;
        }

        const selectors = [
          '#shopify-section-header',
          'sticky-header',
          '.header-wrapper',
          'header.header',
          'header',
          '.header',
          '[class*="header-wrapper"]',
          '[data-section-type="header"]'
        ];

        let headerEl = null;
        for (let i = 0; i < selectors.length; i++) {
          const els = document.querySelectorAll(selectors[i]);
          for (let j = 0; j < els.length; j++) {
            const el = els[j];
            if (el && !this.contains(el)) {
              headerEl = el;
              break;
            }
          }
          if (headerEl) break;
        }

        let height = 0;
        if (headerEl) {
          const style = window.getComputedStyle(headerEl);
          const isHidden = style.display === 'none' || style.visibility === 'hidden' || headerEl.offsetParent === null;

          if (isHidden) {
            height = 0;
          } else {
            const rect = headerEl.getBoundingClientRect();
            const measured = Math.round(rect.height || headerEl.offsetHeight || 0);
            if (measured > 0) {
              height = measured;
            } else {
              height = 0;
            }
          }
        } else {
          height = 0;
        }

        this.style.setProperty('--ha-header-height', height + 'px');
      };

      detect();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', detect, { once: true });
      }
      window.addEventListener('load', detect, { once: true });
      window.addEventListener('resize', detect, { passive: true });
    }

    _readContext() {
      // Check customer_id passed from mobile app via URL query parameters
      const urlParams = new URLSearchParams(window.location.search);
      let paramCustomerId = urlParams.get('customer_id') || urlParams.get('cid') || urlParams.get('shopify_customer_id') || '';

      // Persist / restore customer_id in webview localStorage
      try {
        if (paramCustomerId) {
          localStorage.setItem('ha_app_customer_id', paramCustomerId);
        } else {
          paramCustomerId = localStorage.getItem('ha_app_customer_id') || '';
        }
      } catch (e) {}

      let rawId = paramCustomerId || this.dataset.customerId || '';
      if (rawId && !rawId.startsWith('gid://')) {
        rawId = 'gid://shopify/Customer/' + rawId.trim();
      }

      this.customerId = rawId;
      this.loggedIn = (this.dataset.loggedIn === 'true') || !!this.customerId;
      this.profile = {
        name: this.dataset.customerName || '',
        email: this.dataset.customerEmail || '',
        phone: this.dataset.customerPhone || '',
      };
      Api.init(this.dataset.baseUrl, this.customerId);
      this.shopifyCustomerId = (this.customerId || '').split('/').pop();
      this._levelCompleteInFlight = new Set();
      this.levelMap = {};
      this.courseMap = {};
      this.lectureMap = {};
      this.hycashTotal = 0;
      this.allDone = false;

      this._env = this._detectEnv();
      this.sessionId = this._sessionId();
      this.userType = this.loggedIn ? 'Returning' : 'Guest';
      this._started = { level: new Set(), course: new Set(), lecture: new Set() };
      this._currentScreenId = 'home';
      this._screenEnteredAt = Date.now();
    }

    _detectEnv() {
      let device = 'Desktop', browser = 'Other';
      try {
        const ua = navigator.userAgent || '';
        if (/iPad|Tablet|PlayBook|Silk/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) device = 'Tablet';
        else if (/Mobi|Android|iPhone|iPod|Windows Phone/i.test(ua)) device = 'Mobile';
        if (/Edg\//i.test(ua)) browser = 'Edge';
        else if (/OPR\/|Opera/i.test(ua)) browser = 'Opera';
        else if (/SamsungBrowser/i.test(ua)) browser = 'Samsung Internet';
        else if (/Chrome\//i.test(ua)) browser = 'Chrome';
        else if (/Firefox\//i.test(ua)) browser = 'Firefox';
        else if (/Safari\//i.test(ua)) browser = 'Safari';
      } catch (e) { /* defaults */ }
      return { device: device, browser: browser };
    }

    _sessionId() {
      try {
        const k = 'ha_session_id';
        let id = sessionStorage.getItem(k);
        if (!id) { id = 'sess-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8); sessionStorage.setItem(k, id); }
        return id;
      } catch (e) { return 'sess-' + Date.now().toString(36); }
    }

    _istTimestamp() {
      try {
        const d = new Date(Date.now() + (5.5 * 60 * 60 * 1000));
        const p = (n) => String(n).padStart(2, '0');
        return d.getUTCFullYear() + '-' + p(d.getUTCMonth() + 1) + '-' + p(d.getUTCDate()) +
          'T' + p(d.getUTCHours()) + ':' + p(d.getUTCMinutes()) + ':' + p(d.getUTCSeconds()) + '+05:30';
      } catch (e) { return new Date().toISOString(); }
    }

    _displayName() {
      const n = ((this.profile && this.profile.name) || '').trim();
      if (n) return n;
      const saved = this._savedCertName();
      if (saved) return saved;
      return this._emailName();
    }

    _emailName() {
      const email = ((this.profile && this.profile.email) || '').trim();
      if (email && email.indexOf('@') > 0) {
        const local = email.split('@')[0].split('+')[0];
        if (local && !/^\+?\d[\d\s._-]*$/.test(local)) {
          const pretty = local.replace(/[._+-]+/g, ' ').replace(/\s+/g, ' ').trim()
            .split(' ').map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(' ');
          if (pretty) return pretty;
        }
      }
      return '';
    }

    _certNameKey() { return 'ha_cert_name:' + (this.customerId || 'anon'); }
    _savedCertName() { try { return ((localStorage.getItem(this._certNameKey())) || '').trim(); } catch (e) { return ''; } }
    _saveCertName(v) { try { if (v) localStorage.setItem(this._certNameKey(), v); } catch (e) {} }

    _int(v) { const n = parseInt(v, 10); return isNaN(n) ? (v || '') : n; }

    _buildRelations() {
      this.levelOfCourse = {};
      this.coursesOfLevel = {};
      this.lecturesOfCourse = {};
      this.cat = { level: {}, course: {}, lecture: {} };
      this.querySelectorAll('[data-level-card]').forEach((card) => {
        const lid = card.dataset.levelId;
        this.coursesOfLevel[lid] = (card.dataset.courses || '').split(',').filter(Boolean);
        this.cat.level[lid] = {
          id: lid, name: card.dataset.levelName || '',
          number: card.dataset.levelNumber || '', position: card.dataset.cardPosition || '',
        };
      });
      this.querySelectorAll('[data-course-card]').forEach((card) => {
        const cid = card.dataset.courseId;
        this.levelOfCourse[cid] = card.dataset.levelId;
        this.lecturesOfCourse[cid] = (card.dataset.lectures || '').split(',').filter(Boolean);
        this.cat.course[cid] = {
          id: cid, name: card.dataset.courseName || '',
          number: card.dataset.courseNumber || '', position: card.dataset.cardPosition || '',
          levelId: card.dataset.levelId || '',
        };
      });
      this.querySelectorAll('[data-lecture-item]').forEach((item) => {
        const lid = item.dataset.lectureId;
        this.cat.lecture[lid] = {
          id: lid, name: item.dataset.lectureName || '',
          number: item.dataset.lectureNumber || '', position: item.dataset.cardPosition || '',
          courseId: item.dataset.courseId || '',
        };
      });
    }

    _hier(opts) {
      opts = opts || {};
      const out = {};
      let levelId = opts.levelId, courseId = opts.courseId, lectureId = opts.lectureId;
      if (lectureId) {
        const L = this.cat.lecture[lectureId];
        out['Lecture ID'] = lectureId;
        if (L) { out['Lecture Name'] = L.name; out['Lecture Number'] = this._int(L.number); if (!courseId) courseId = L.courseId; }
      }
      if (courseId) {
        const C = this.cat.course[courseId];
        out['Course ID'] = courseId;
        if (C) { out['Course Name'] = C.name; out['Course Number'] = this._int(C.number); if (!levelId) levelId = C.levelId; }
        else if (!levelId) levelId = this.levelOfCourse[courseId];
      }
      if (levelId) {
        const V = this.cat.level[levelId];
        out['Level ID'] = levelId;
        if (V) { out['Level Name'] = V.name; out['Level Number'] = this._int(V.number); }
      }
      return out;
    }

    /* ----- CleverTap tracking ----- */
    _eventProps(extra) {
      const base = {
        'Customer ID': this.customerId || '',
        'Customer Email': (this.profile && this.profile.email) || '',
        'Customer Name': this._displayName(),
        'Logged In': !!this.loggedIn,
        'User Type': this.userType || '',
        'Session ID': this.sessionId || '',
        'Page URL': (window.location && window.location.href) || '',
        'Referrer': (document && document.referrer) || '',
        'Device Type': this._env ? this._env.device : '',
        'Browser': this._env ? this._env.browser : '',
        'Timestamp': this._istTimestamp(),
        'Source': 'Skin Academy Web',
      };
      if (extra) {
        Object.keys(extra).forEach((k) => {
          const v = extra[k];
          if (v !== undefined && v !== null && v !== '') base[k] = v;
        });
      }
      return base;
    }

    _track(name, extra) {
      try {
        CT.push(name, this._eventProps(extra));
      } catch (e) { /* never throw */ }
    }

    _screenType(id) {
      if (!id) return '';
      if (id === 'home') return 'Home';
      if (id.indexOf('screen-level-') === 0) return 'Level Detail';
      if (id.indexOf('screen-course-') === 0) return 'Course Detail';
      if (id.indexOf('screen-lecture-') === 0) return 'Lecture';
      if (id.indexOf('screen-quiz-') === 0) return 'Quiz';
      return id;
    }

    _ctxOf(el) {
      if (!el || !el.closest) return {};
      const lvl = el.closest('[data-level-id]');
      const crs = el.closest('[data-course-id]');
      const lec = el.closest('[data-lecture-id]');
      const props = this._hier({
        levelId: lvl && lvl.dataset.levelId,
        courseId: crs && crs.dataset.courseId,
        lectureId: lec && lec.dataset.lectureId,
      });
      const screen = el.closest('[data-screen]');
      if (screen) props['Screen Name'] = this._screenType(screen.dataset.screen);
      return props;
    }

    _label(el) {
      return ((el && el.textContent) || '').replace(/\s+/g, ' ').trim();
    }

    _navCta(navEl) {
      const lec = navEl.closest('[data-lecture-item]');
      if (lec) return { name: 'Lecture ' + (lec.dataset.lectureNumber || '') + ' Clicked', type: 'Lecture Item', position: this._int(lec.dataset.cardPosition) };
      const crs = navEl.closest('[data-course-card]');
      if (crs) return { name: 'Course ' + (crs.dataset.courseNumber || '') + ' Clicked', type: 'Course Card', position: this._int(crs.dataset.cardPosition) };
      const lvl = navEl.closest('[data-level-card]');
      if (lvl) return { name: 'Level ' + (lvl.dataset.levelNumber || '') + ' Clicked', type: 'Level Tab', position: this._int(lvl.dataset.cardPosition) };
      const label = this._label(navEl);
      const isBack = /←|\bback\b/i.test(label) || navEl.classList.contains('academy-topbar__back');
      return { name: isBack ? 'Back' : (label || 'Navigate'), type: isBack ? 'Back' : 'Link', position: '' };
    }

    async boot() {
      if (!this.loggedIn) { this._showGate(); return; }
      this._showLoading(true);
      try {
        let data = await Api.dashboard();
        const isNewUser = !data.userExists;
        if (isNewUser && this._hasOnboardingSlides()) {
          this._showLoading(false);
          this._showOnboarding();
          return;
        }
        if (isNewUser) {
          await Api.session(this.profile);
          data = await Api.dashboard();
        }
        this._applyDashboard(data.dashboard);
        this.userType = isNewUser ? 'New' : 'Returning';
        this._fireLoadTracking(isNewUser);
      } catch (e) {
        this.toast.show('Could not load your progress. Please refresh.');
      } finally {
        this._showLoading(false);
      }
    }

    async refreshDashboard() {
      try {
        const data = await Api.dashboard();
        this._applyDashboard(data.dashboard);
      } catch (e) { /* keep last good state */ }
    }

    _applyDashboard(dash) {
      if (!dash) return;
      this.levelMap = {}; this.courseMap = {}; this.lectureMap = {};
      this.allDone = !!dash.allDone;
      this.notificationSubscription = !!dash.notificationSubscription;
      try {
        this.dispatchEvent(new CustomEvent('academy:dashboard-loaded', {
          detail: { dashboard: dash, notificationSubscription: this.notificationSubscription },
          bubbles: true
        }));
      } catch (e) {}
      this.hycashTotal = (dash.hycash && dash.hycash.total) || 0;

      (dash.levels || []).forEach((lvl) => {
        this.levelMap[lvl.levelId] = lvl;
        (lvl.courses || []).forEach((c) => {
          c._levelId = lvl.levelId;
          this.courseMap[c.courseId] = c;
          (c.lectures || []).forEach((lec) => { this.lectureMap[lec.lectureId] = lec; });
        });
        if (lvl.isLevelDone) this._notifyLevelComplete(lvl);
      });
      this.hydrate();
    }

    _notifyLevelComplete(lvl) {
      if (!lvl || !lvl.isLevelDone || lvl.hycashCredited) return;
      const level = lvl.order;
      const levelId = lvl.levelId;
      if (!level || !this.shopifyCustomerId) return;
      if (this._levelCompleteInFlight.has(level)) return;
      this._levelCompleteInFlight.add(level);
      this._track('Academy Level Completed', Object.assign(
        { 'Level': level, 'HYcash Earned': this._levelHycash(levelId) },
        this._hier({ levelId: levelId })));
      Api.levelComplete(level, this.shopifyCustomerId, levelId)
        .then(() => this.refreshDashboard())
        .catch(() => {})
        .finally(() => { this._levelCompleteInFlight.delete(level); });
    }

    isLectureCompleted(id) { const l = this.lectureMap[id]; return !!(l && l.isCompleted); }
    isCourseUnlocked(id) { const c = this.courseMap[id]; return !!(c && c.isUnlocked); }
    isCourseDone(id) { const c = this.courseMap[id]; return !!(c && c.isCourseDone); }
    isCourseQuizUnlocked(id) { const c = this.courseMap[id]; return !!(c && c.quizUnlocked); }
    isCourseQuizSubmitted(id) { const c = this.courseMap[id]; return !!(c && c.quizSubmitted); }
    isLevelUnlocked(id) { const l = this.levelMap[id]; return !!(l && l.isUnlocked); }
    isLevelDone(id) { const l = this.levelMap[id]; return !!(l && l.isLevelDone); }

    _nextCourseId(cid) {
      const lvl = this.levelOfCourse[cid] || (this.courseMap[cid] && this.courseMap[cid]._levelId);
      const list = (this.coursesOfLevel[lvl] || []).slice()
        .sort((a, b) => ((this.courseMap[a] || {}).order || 0) - ((this.courseMap[b] || {}).order || 0));
      const i = list.indexOf(cid);
      return (i >= 0 && i < list.length - 1) ? list[i + 1] : null;
    }

    _fmtDate(iso) {
      if (!iso) return '';
      try { return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }); }
      catch (e) { return ''; }
    }

    _fmtWhen(iso) {
      if (!iso) return '';
      try {
        return new Date(iso).toLocaleString(undefined, {
          day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
        });
      } catch (e) { return ''; }
    }

    hydrate() {
      this._hydrateLevels();
      this._hydrateCoursesHero();
      this._hydrateCourses();
      this._hydrateLectures();
      this._hydrateCourseDetails();
      this._hydrateJourney();
      const finalBtn = this.querySelector('[data-final-cert]');
      if (finalBtn) finalBtn.hidden = !this.allDone;
    }

    _hydrateCoursesHero() {
      this.querySelectorAll('[data-screen^="screen-level-"]').forEach((screen) => {
        const levelId = screen.dataset.screen.replace('screen-level-', '');
        const courseIds = this.coursesOfLevel[levelId] || [];
        const total = courseIds.length;
        const done = courseIds.filter((cid) => this.isCourseDone(cid)).length;
        const pct = total > 0 ? Math.round((done / total) * 100) : 0;
        const lbl = screen.querySelector('[data-courses-progress-label]');
        const pc = screen.querySelector('[data-courses-progress-pct]');
        const fill = screen.querySelector('[data-courses-progress-fill]');
        if (lbl) lbl.textContent = done + ' of ' + total + ' courses done';
        if (pc) pc.textContent = pct + '%';
        if (fill) fill.style.width = pct + '%';
      });
    }

    _hydrateLevels() {
      this.querySelectorAll('[data-level-card]').forEach((card) => {
        const id = card.dataset.levelId;
        const st = this.levelMap[id];
        const unlocked = st ? st.isUnlocked : (card.dataset.levelIndex === '0');
        const done = !!(st && st.isLevelDone);
        card.classList.toggle('is-locked', !unlocked);
        card.classList.toggle('is-completed', done);

        const badge = card.querySelector('[data-level-badge]');
        if (badge) badge.textContent = done ? '✓ Done' : (unlocked ? '● Open' : '🔒 Locked');

        const certBtn = card.querySelector('[data-cert-btn]');
        if (certBtn) certBtn.classList.toggle('is-locked', !done);

        const total = st ? st.totalLectures : 0;
        const completed = st ? st.completedLectures : 0;
        const pct = st ? (st.progressPercent || 0) : 0;
        const courseIds = this.coursesOfLevel[id] || [];
        const doneCourses = courseIds.filter((cid) => this.isCourseDone(cid)).length;

        const prog = card.querySelector('[data-level-progress]');
        const hint = card.querySelector('[data-level-lock-hint]');
        const cta = card.querySelector('[data-level-cta]');
        if (prog) {
          if (unlocked) {
            prog.hidden = false;
            const lbl = prog.querySelector('[data-level-progress-label]');
            const pc = prog.querySelector('[data-level-progress-pct]');
            const fill = prog.querySelector('[data-level-progress-fill]');
            if (lbl) lbl.textContent = doneCourses + ' of ' + courseIds.length + ' courses done';
            if (pc) pc.textContent = pct + '%';
            if (fill) fill.style.width = pct + '%';
          } else { prog.hidden = true; }
        }

        if (hint) {
          hint.hidden = unlocked;
          if (!unlocked) {
            if (st && st.unlocksAt) {
              const when = this._fmtWhen(st.unlocksAt);
              hint.textContent = '🔒 Unlocks on ' + when;
              card.dataset.lockedMsg = 'This level unlocks on ' + when;
            } else {
              hint.textContent = '🔒 Complete the previous level to unlock';
              card.dataset.lockedMsg = 'Complete the previous level to unlock this one.';
            }
          }
        }

        const overlayText = card.querySelector('[data-level-overlay-text]');
        if (overlayText && !unlocked) {
          if (st && st.unlocksAt) {
            const when = this._fmtWhen(st.unlocksAt);
            overlayText.textContent = 'Unlocks on ' + when;
          } else {
            const defaultTxt = card.dataset.lockedText || this.dataset.defaultLockedText || 'The Level will open soon';
            overlayText.textContent = defaultTxt;
          }
        }

        if (cta) {
          cta.hidden = false;
          if (unlocked) {
            cta.classList.remove('level-card__cta--locked');
            cta.classList.add('level-card__cta--active');
            cta.textContent = done ? '✓ Completed — Review' : (completed > 0 ? '▶ Continue Learning' : '▶ Start Learning');
          } else {
            cta.classList.remove('level-card__cta--active');
            cta.classList.add('level-card__cta--locked');
            cta.textContent = '🔒 Locked';
          }
        }
      });
    }

    _hydrateCourses() {
      this.querySelectorAll('[data-course-card]').forEach((card) => {
        const id = card.dataset.courseId;
        const st = this.courseMap[id];
        const unlocked = st ? st.isUnlocked : (card.dataset.courseIndex === '0');
        const done = !!(st && st.isCourseDone);
        card.classList.toggle('is-locked', !unlocked);
        card.classList.toggle('is-completed', done);

        const num = card.querySelector('[data-course-number]');
        if (num) num.textContent = done ? '✓' : (parseInt(card.dataset.courseIndex, 10) + 1);

        const tag = card.querySelector('[data-course-status-tag]');
        if (tag) tag.textContent = done ? '✅ Completed' : (unlocked ? '🟢 Unlocked' : '🔒 Locked');

        const hint = card.querySelector('[data-course-hint]');
        if (hint) {
          if (unlocked) {
            hint.hidden = true;
          } else if (st && st.unlocksAt) {
            const when = this._fmtWhen(st.unlocksAt);
            hint.hidden = false;
            hint.textContent = '🔒 Unlocks on ' + when;
            card.dataset.lockedMsg = 'This course unlocks on ' + when;
          } else {
            hint.hidden = false;
            hint.textContent = '🔒 Complete the previous course to unlock';
            card.dataset.lockedMsg = 'Complete the previous course to unlock this one.';
          }
        }

        const hc = card.querySelector('[data-course-hycash]');
        if (hc) {
          if (done && st && st.hycashEarned > 0) { hc.hidden = false; hc.textContent = 'H +' + st.hycashEarned; }
          else hc.hidden = true;
        }

        const btn = card.querySelector('[data-course-btn]');
        if (btn) {
          btn.className = 'course-card__btn ' + (done ? 'course-card__btn--done' : (unlocked ? 'course-card__btn--go' : 'course-card__btn--locked'));
          btn.textContent = done ? 'Review' : (unlocked ? 'Begin' : 'Locked');
        }
      });
    }

    _hydrateLectures() {
      this.querySelectorAll('[data-lecture-item]').forEach((item) => {
        const id = item.dataset.lectureId;
        const done = this.isLectureCompleted(id);
        item.classList.toggle('is-done', done);
        const icon = item.querySelector('[data-lecture-icon]');
        if (icon) icon.textContent = done ? '✓' : '▶';
        const ind = item.querySelector('[data-lecture-indicator]');
        if (ind) ind.textContent = done ? '✓' : '▶';
        const flag = item.querySelector('[data-lecture-done-flag]');
        if (flag) flag.hidden = !done;
      });
      this.querySelectorAll('[data-screen^="screen-lecture-"]').forEach((screen) => {
        const id = screen.dataset.lectureId;
        const done = this.isLectureCompleted(id);
        const badge = screen.querySelector('[data-lecture-done-badge]');
        if (badge) badge.hidden = !done;
        const btn = screen.querySelector('[data-lecture-watched-btn]');
        if (btn) {
          btn.disabled = done;
          btn.textContent = done ? 'Watched ✓' : 'Mark as Watched ✓';
        }
        const nextBtn = screen.querySelector('[data-lecture-next]');
        if (nextBtn) {
          nextBtn.disabled = !done;
        }
        const quizBtn = screen.querySelector('[data-lecture-quiz-btn]');
        if (quizBtn) {
          quizBtn.disabled = !done;
        }
      });
    }

    _hydrateCourseDetails() {
      this.querySelectorAll('[data-screen^="screen-course-"]').forEach((screen) => {
        const id = screen.dataset.courseId;
        const st = this.courseMap[id];

        const quizBtn = screen.querySelector('[data-course-quiz-btn]');
        if (quizBtn) {
          const submitted = this.isCourseQuizSubmitted(id);
          const unlocked = this.isCourseQuizUnlocked(id);
          quizBtn.classList.toggle('is-locked', !unlocked && !submitted);
          quizBtn.classList.toggle('is-submitted', submitted);
          if (submitted) quizBtn.textContent = '✓ Review Course Quiz';
          else if (unlocked) quizBtn.textContent = '✏️ Take Course Quiz';
          else quizBtn.textContent = '🔒 Course Quiz — finish all lectures';
        }

        this.querySelectorAll('[data-lecture-quiz-btn]').forEach((lecQuizBtn) => {
          const lcid = lecQuizBtn.dataset.courseId;
          const submitted = this.isCourseQuizSubmitted(lcid);
          const unlocked = this.isCourseQuizUnlocked(lcid);
          const next = this._nextCourseId(lcid);
          const nextUnlocked = next ? this.isCourseUnlocked(next) : false;

          if (submitted) {
            lecQuizBtn.classList.remove('is-locked');
            if (next && nextUnlocked) lecQuizBtn.textContent = 'Next';
            else if (next) lecQuizBtn.textContent = '🔒 Next';
            else lecQuizBtn.textContent = '✓ Quiz';
          } else if (unlocked) {
            lecQuizBtn.classList.remove('is-locked');
            lecQuizBtn.textContent = 'Quiz';
          } else {
            lecQuizBtn.classList.add('is-locked');
            lecQuizBtn.textContent = '🔒 Quiz';
          }
        });

        const startBtn = screen.querySelector('[data-course-start]');
        const note = screen.querySelector('[data-course-note]');
        const lectures = this.lecturesOfCourse[id] || [];
        const firstUndone = lectures.find((lid) => !this.isLectureCompleted(lid));
        const allDoneLec = lectures.length > 0 && !firstUndone;
        const hasQuiz = !!(st && st.hasQuiz);
        const quizSubmitted = this.isCourseQuizSubmitted(id);
        const courseDone = this.isCourseDone(id);
        const next = this._nextCourseId(id);
        const nextUnlockWhen = this._fmtWhen((next && this.courseMap[next] && this.courseMap[next].unlocksAt) || (st && st.nextCourseUnlocksAt));

        if (startBtn) {
          if (!allDoneLec) {
            startBtn.hidden = false;
            startBtn.dataset.startMode = 'lecture';
            startBtn.textContent = lectures.some((lid) => this.isLectureCompleted(lid)) ? '▶ Continue' : '▶ Start Lecture 1';
          } else if (hasQuiz && !quizSubmitted) {
            startBtn.hidden = true;
          } else if (next && this.isCourseUnlocked(next)) {
            startBtn.hidden = false;
            startBtn.dataset.startMode = 'next';
            startBtn.dataset.nextCourseId = next;
            startBtn.textContent = 'Next Course →';
          } else if (next) {
            startBtn.hidden = false;
            startBtn.dataset.startMode = 'next-locked';
            startBtn.dataset.nextCourseId = next;
            startBtn.textContent = '🔒 Next Course Locked';
          } else {
            startBtn.hidden = false;
            startBtn.dataset.startMode = 'home';
            startBtn.textContent = '✓ Back to Home';
          }
        }

        if (note) {
          if (allDoneLec && hasQuiz && !quizSubmitted) {
            note.textContent = '✓ All lectures complete — take the quiz to finish this course and unlock what\u2019s next.';
            note.classList.add('is-quiz');
            note.hidden = false;
          } else if (courseDone && next && !this.isCourseUnlocked(next)) {
            note.textContent = nextUnlockWhen ? ('🎉 Course complete! The next course unlocks on ' + nextUnlockWhen + '.') : '🎉 Course complete! The next course isn\u2019t available yet.';
            note.classList.remove('is-quiz');
            note.hidden = false;
          } else {
            note.hidden = true;
          }
        }
      });
    }

    _hydrateJourney() {
      const wrap = this.querySelector('[data-hcj]');
      if (!wrap) return;
      const max = parseInt(wrap.dataset.hcjMax, 10) || 0;
      const total = this.hycashTotal || 0;
      const cur = wrap.querySelector('[data-hcj-current]');
      if (cur) cur.textContent = total + ' HC';
      const fill = wrap.querySelector('[data-hcj-fill]');
      if (fill) fill.style.width = (max > 0 ? Math.min(100, Math.round((total / max) * 100)) : 0) + '%';
      wrap.querySelectorAll('[data-hcj-ms]').forEach((ms) => {
        const v = parseInt(ms.dataset.value, 10) || 0;
        const reached = total >= v;
        ms.querySelector('.hcj-ms-dot')?.classList.toggle('reached', reached);
        ms.querySelector('.hcj-ms-val')?.classList.toggle('reached', reached);
      });
    }

    _showLoading(on) { if (this.loadingEl) this.loadingEl.hidden = !on; }
    _showGate() {
      if (!this.gateEl) return;
      this.gateEl.hidden = false;
      if (!this._gateTracked) { this._gateTracked = true; this._track('Academy Login Gate Shown'); }
      const btn = this.gateEl.querySelector('[data-gate-login]');
      if (btn) btn.onclick = () => this._login(btn);

      const homeBtn = this.gateEl.querySelector('[data-gate-home]');
      if (homeBtn) homeBtn.onclick = () => {
        const home = this.dataset.homeRedirect || '/';
        this._track('Academy CTA Clicked', { 'CTA Name': 'Go to Homepage', 'CTA Type': 'Auth', 'Screen Name': 'Login Gate', 'Redirect Path': home });
        window.location.href = home;
      };
    }

    _login(btn) {
      if (this._loginStarted) return;
      const redirect = this.dataset.loginRedirect || '/';
      const url = window.location.origin + redirect;
      this._track('Academy Login Clicked', {
        'Redirect Path': redirect,
        'CTA Name': 'Sign In', 'CTA Type': 'Auth', 'Screen Name': 'Login Gate',
      });
      try { sessionStorage.setItem('ha_logging_in', '1'); } catch (e) {}

      if (btn) {
        btn.disabled = true;
        btn.classList.add('is-loading');
        btn.innerHTML = '<span class="academy-gate__spinner" aria-hidden="true"></span>Logging in…';
      }

      const fire = () => {
        this._loginStarted = true;
        try { window.kpHandleLogin(url); }
        catch (e) {
          this._loginStarted = false;
          window.location.href = '/account/login?return_url=' + encodeURIComponent(redirect);
        }
      };

      if (typeof window.kpHandleLogin === 'function') { fire(); return; }

      let tries = 0;
      const iv = setInterval(() => {
        tries += 1;
        if (typeof window.kpHandleLogin === 'function') { clearInterval(iv); fire(); }
        else if (tries > 20) { // ~5s
          clearInterval(iv);
          window.location.href = '/account/login?return_url=' + encodeURIComponent(redirect);
        }
      }, 250);
    }

    _hasOnboardingSlides() {
      const el = this.querySelector('[data-onboarding]');
      return !!(el && parseInt(el.dataset.onboardingTotal, 10) > 0);
    }

    _showOnboarding() {
      const overlay = this.querySelector('[data-onboarding]');
      if (!overlay) return;

      this._onboardingEl = overlay;
      this._onboardingSlides = Array.from(overlay.querySelectorAll('[data-onboarding-slide]'));
      this._onboardingTotal = this._onboardingSlides.length;

      let savedIndex = 0;
      try { savedIndex = parseInt(sessionStorage.getItem('ha_onboarding_slide'), 10) || 0; } catch (e) {}
      if (savedIndex >= this._onboardingTotal) savedIndex = 0;
      this._onboardingIndex = savedIndex;

      overlay.hidden = false;
      document.body.style.overflow = 'hidden';

      this._syncOnboarding();
      this._initOnboardingSwipe();

      if (savedIndex > 0) {
        const container = overlay.querySelector('[data-onboarding-slides]');
        if (container) {
          requestAnimationFrame(() => {
            container.scrollLeft = savedIndex * container.offsetWidth;
          });
        }
      }

      if (!this._onboardingWired) {
        this._onboardingWired = true;
        const skipBtn = overlay.querySelector('[data-onboarding-skip]');
        if (skipBtn) skipBtn.addEventListener('click', () => this._closeOnboarding('Skip'));
        const nextBtn = overlay.querySelector('[data-onboarding-next]');
        if (nextBtn) nextBtn.addEventListener('click', () => this._onboardingNext());
        const backBtn = overlay.querySelector('[data-onboarding-back]');
        if (backBtn) backBtn.addEventListener('click', () => this._onboardingPrev());
      }

      this._track('Academy Onboarding Shown', { 'Total Slides': this._onboardingTotal, 'Resumed Slide': savedIndex + 1 });
    }

    _hideOnboarding() {
      if (this._onboardingEl) {
        this._onboardingEl.hidden = true;
        document.body.style.overflow = '';
      }
      try { sessionStorage.removeItem('ha_onboarding_slide'); } catch (e) {}
    }

    async _closeOnboarding(source) {
      this._track('Academy Onboarding Completed', {
        'Exit Source': source,
        'Slides Viewed': this._onboardingIndex + 1,
        'Total Slides': this._onboardingTotal,
      });

      this._hideOnboarding();
      this._showLoading(true);

      try {
        await Api.session(this.profile);
        const data = await Api.dashboard();
        this._applyDashboard(data.dashboard);
        this.userType = 'New';
        this._fireLoadTracking(true);
      } catch (e) {
        this.toast.show('Could not load your progress. Please refresh.');
      } finally {
        this._showLoading(false);
      }
    }

    _syncOnboarding() {
      if (!this._onboardingEl) return;
      const idx = this._onboardingIndex;
      const total = this._onboardingTotal;
      const isFirst = idx === 0;
      const isLast = idx === total - 1;

      this._onboardingEl.querySelectorAll('.onboarding-progress__segment').forEach((seg, i) => {
        seg.classList.toggle('is-active', i <= idx);
      });

      const backBtn = this._onboardingEl.querySelector('[data-onboarding-back]');
      if (backBtn) backBtn.hidden = isFirst;

      const nextBtn = this._onboardingEl.querySelector('[data-onboarding-next]');
      if (nextBtn) {
        nextBtn.textContent = isLast ? 'Start Now' : 'Next';
        nextBtn.classList.toggle('onboarding-next--final', isLast);
      }

      try { sessionStorage.setItem('ha_onboarding_slide', String(idx)); } catch (e) {}
    }

    _onboardingNext() {
      if (this._onboardingIndex < this._onboardingTotal - 1) {
        this._onboardingIndex++;
        this._scrollToSlide(this._onboardingIndex);
        this._syncOnboarding();
        this._track('Academy Onboarding Slide Viewed', {
          'Slide Number': this._onboardingIndex + 1,
          'Total Slides': this._onboardingTotal,
          'Navigation': 'Button',
        });
      } else {
        this._closeOnboarding('Start Now');
      }
    }

    _onboardingPrev() {
      if (this._onboardingIndex > 0) {
        this._onboardingIndex--;
        this._scrollToSlide(this._onboardingIndex);
        this._syncOnboarding();
      }
    }

    _scrollToSlide(index) {
      const container = this._onboardingEl && this._onboardingEl.querySelector('[data-onboarding-slides]');
      if (!container) return;
      container.scrollTo({ left: index * container.offsetWidth, behavior: 'smooth' });
    }

    _initOnboardingSwipe() {
      const container = this._onboardingEl && this._onboardingEl.querySelector('[data-onboarding-slides]');
      if (!container || this._onboardingSwipeInit) return;
      this._onboardingSwipeInit = true;

      let scrollTimer;
      container.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          const w = container.offsetWidth;
          if (!w) return;
          const newIndex = Math.round(container.scrollLeft / w);
          if (newIndex !== this._onboardingIndex && newIndex >= 0 && newIndex < this._onboardingTotal) {
            const prev = this._onboardingIndex;
            this._onboardingIndex = newIndex;
            this._syncOnboarding();
            if (newIndex > prev) {
              this._track('Academy Onboarding Slide Viewed', {
                'Slide Number': newIndex + 1,
                'Total Slides': this._onboardingTotal,
                'Navigation': 'Swipe',
              });
            }
          }
        }, 120);
      }, { passive: true });
    }

    _fireLoadTracking(isNewUser) {
      if (!this._loadTracked) {
        this._loadTracked = true;
        if (isNewUser) this._track('Academy Sign Up Completed', { 'Customer Phone': this.profile.phone });
        let justLoggedIn = false;
        try { justLoggedIn = sessionStorage.getItem('ha_logging_in') === '1'; if (justLoggedIn) sessionStorage.removeItem('ha_logging_in'); } catch (e) {}
        if (justLoggedIn || isNewUser) this._track('Academy User Signed In', { 'Is New User': isNewUser });
        this._track('Academy Loaded', {
          'Total Levels': Object.keys(this.levelMap).length,
          'Levels Completed': Object.keys(this.levelMap).filter((id) => this.levelMap[id].isLevelDone).length,
          'HYcash Total': this.hycashTotal,
          'All Levels Completed': !!this.allDone,
        });
      }
    }

    _bindClicks() {
      this.addEventListener('click', (e) => {
        const certBtn = e.target.closest('[data-cert-btn]');
        if (certBtn) {
          e.stopPropagation();
          const lid = certBtn.dataset.levelId;
          if (this.isLevelDone(lid)) {
            this._openCert({
              title: certBtn.dataset.levelName, sub: certBtn.dataset.levelSub,
              desc: certBtn.dataset.levelDesc || '',
              name: this._displayName(), hycash: this._levelHycash(lid), final: false,
              template: certBtn.dataset.certTemplate || '', levelId: lid,
            });
          } else {
            this._track('Academy Certificate Locked Tapped', Object.assign(
              { 'CTA Name': 'View Certificate', 'CTA Type': 'Certificate',
                'CTA Position': this._int((certBtn.closest('[data-level-card]') || {}).dataset ? certBtn.closest('[data-level-card]').dataset.cardPosition : '') },
              this._hier({ levelId: lid })));
            this.toast.show('Complete all courses in this level to unlock your certificate.');
          }
          return;
        }
        const finalBtn = e.target.closest('[data-final-cert]');
        if (finalBtn) {
          this._openCert({
            title: 'HYPHEN Academy Graduate', sub: 'All levels completed',
            name: this._displayName(), hycash: this.hycashTotal, final: true,
            template: finalBtn.dataset.certTemplate || '',
          });
          return;
        }
        const watchBtn = e.target.closest('[data-lecture-watched-btn]');
        if (watchBtn) {
          if (!watchBtn.disabled) this._completeLecture(watchBtn.dataset.lectureId, watchBtn.dataset.courseId, 100, 'Button');
          return;
        }
        const startBtn = e.target.closest('[data-course-start]');
        if (startBtn) {
          const cid = startBtn.dataset.courseId;
          const mode = startBtn.dataset.startMode || 'lecture';
          if (mode === 'quiz') {
            this._track('Academy CTA Clicked', Object.assign(
              { 'CTA Name': 'Take Quiz to Finish', 'CTA Type': 'Quiz Gate' }, this._ctxOf(startBtn)));
            this.navigate('screen-quiz-' + cid);
            return;
          }
          if (mode === 'next') {
            const nid = startBtn.dataset.nextCourseId;
            this._track('Academy CTA Clicked', Object.assign(
              { 'CTA Name': 'Next Course', 'CTA Type': 'Course Nav', 'Target Course ID': nid,
                'Target Course Name': (this.courseMap[nid] || {}).title }, this._ctxOf(startBtn)));
            this.navigate('screen-course-' + nid);
            return;
          }
          if (mode === 'next-locked') {
            const nid = startBtn.dataset.nextCourseId;
            const when = this._fmtWhen((this.courseMap[nid] && this.courseMap[nid].unlocksAt) || (this.courseMap[cid] && this.courseMap[cid].nextCourseUnlocksAt));
            this._track('Academy CTA Clicked', Object.assign(
              { 'CTA Name': 'Next Course Locked', 'CTA Type': 'Course Nav' }, this._ctxOf(startBtn)));
            this.toast.show(when ? ('The next course unlocks on ' + when + '.') : 'The next course is locked for now.');
            const levelId = (cid && this.levelOfCourse[cid]) || (cid && this.courseMap[cid] && this.courseMap[cid]._levelId);
            if (levelId) this.navigate('screen-level-' + levelId);
            else if (cid) this.navigate('screen-course-' + cid);
            else this.navigate('home');
            return;
          }
          if (mode === 'home') {
            this._track('Academy CTA Clicked', Object.assign(
              { 'CTA Name': 'Back to Home', 'CTA Type': 'Course Nav' }, this._ctxOf(startBtn)));
            this.navigate('home');
            return;
          }
          // default: go to first not-completed lecture
          const lectures = this.lecturesOfCourse[cid] || [];
          if (!lectures.length) {
            this._track('Academy CTA Clicked', Object.assign(
              { 'CTA Name': this._label(startBtn), 'CTA Type': 'Start Lecture', 'Has Lectures': false },
              this._ctxOf(startBtn)));
            this.toast.show('Lectures coming soon.'); return;
          }
          const target = lectures.find((lid) => !this.isLectureCompleted(lid)) || lectures[0];
          this._track('Academy CTA Clicked', Object.assign(
            { 'CTA Name': this._label(startBtn), 'CTA Type': 'Start Lecture',
              'Target Lecture ID': target, 'Target Lecture Name': (this.cat.lecture[target] || {}).name },
            this._ctxOf(startBtn)));
          this.navigate('screen-lecture-' + target);
          return;
        }
        // Next lecture (lecture detail)
        const nextLecBtn = e.target.closest('[data-lecture-next]');
        if (nextLecBtn) {
          const nid = nextLecBtn.dataset.nextLectureId;
          const l = this.lectureMap[nid];
          const unlocked = l ? !!l.isUnlocked : true;
          this._track('Academy CTA Clicked', Object.assign(
            { 'CTA Name': 'Next Lecture', 'CTA Type': 'Lecture Nav', 'Target Lecture ID': nid,
              'Target Lecture Name': (this.cat.lecture[nid] || {}).name, 'Unlocked': unlocked },
            this._ctxOf(nextLecBtn)));
          if (!unlocked) { this.toast.show('Finish this lecture to unlock the next one.'); return; }
          this.navigate('screen-lecture-' + nid);
          return;
        }
        // Course quiz button (gate)
        const quizBtn = e.target.closest('[data-course-quiz-btn]');
        if (quizBtn) {
          const cid = quizBtn.dataset.courseId;
          const submitted = this.isCourseQuizSubmitted(cid);
          const unlocked = this.isCourseQuizUnlocked(cid);
          this._track('Academy CTA Clicked', Object.assign(
            { 'CTA Name': 'Course Quiz', 'CTA Type': 'Quiz Gate',
              'Quiz Status': submitted ? 'Submitted' : (unlocked ? 'Unlocked' : 'Locked') },
            this._ctxOf(quizBtn)));
          if (!unlocked && !submitted) {
            this.toast.show('Finish all lectures in this course to unlock the quiz.');
            return;
          }
          this.navigate(quizBtn.dataset.nav);
          return;
        }
        const lecQuizBtn = e.target.closest('[data-lecture-quiz-btn]');
        if (lecQuizBtn) {
          const cid = lecQuizBtn.dataset.courseId;
          const submitted = this.isCourseQuizSubmitted(cid);
          const unlocked = this.isCourseQuizUnlocked(cid);
          const next = this._nextCourseId(cid);
          const levelId = (cid && this.levelOfCourse[cid]) || (cid && this.courseMap[cid] && this.courseMap[cid]._levelId);

          this._track('Academy CTA Clicked', Object.assign(
            { 'CTA Name': 'Lecture Quiz Button', 'CTA Type': 'Quiz Gate',
              'Quiz Status': submitted ? 'Submitted' : (unlocked ? 'Unlocked' : 'Locked') },
            this._ctxOf(lecQuizBtn)));

          if (submitted) {
            if (next && this.isCourseUnlocked(next)) {
              const nLecs = this.lecturesOfCourse[next] || [];
              if (nLecs.length) this.navigate('screen-lecture-' + nLecs[0]);
              else this.navigate('screen-course-' + next);
            } else if (next) {
              const when = this._fmtWhen((this.courseMap[next] && this.courseMap[next].unlocksAt) || (this.courseMap[cid] && this.courseMap[cid].nextCourseUnlocksAt));
              this.toast.show(when ? ('The next course unlocks on ' + when + '.') : 'The next course is locked for now.');
              if (levelId) this.navigate('screen-level-' + levelId);
              else if (cid) this.navigate('screen-course-' + cid);
              else this.navigate('home');
            } else {
              if (levelId) this.navigate('screen-level-' + levelId);
              else if (cid) this.navigate('screen-course-' + cid);
              else this.navigate('home');
            }
            return;
          }

          if (!unlocked) {
            this.toast.show('Finish all lectures in this course to unlock the quiz.');
            if (levelId) this.navigate('screen-level-' + levelId);
            else if (cid) this.navigate('screen-course-' + cid);
            else this.navigate('home');
            return;
          }

          this.navigate(lecQuizBtn.dataset.nav || ('screen-quiz-' + cid));
          return;
        }
        const lockedCard = e.target.closest('.level-card.is-locked, .course-card.is-locked');
        if (lockedCard) {
          this._track('Academy Locked Item Tapped', Object.assign(
            { 'Item Type': lockedCard.classList.contains('level-card') ? 'Level' : 'Course',
              'CTA Position': this._int(lockedCard.dataset.cardPosition),
              'Locked Message': lockedCard.dataset.lockedMsg || '' },
            this._ctxOf(lockedCard)));
          this.toast.show(lockedCard.dataset.lockedMsg || '🔒 Locked');
          return;
        }
        const navEl = e.target.closest('[data-nav]');
        if (navEl) {
          if (navEl.closest('.level-card.is-locked, .course-card.is-locked')) return;
          const cta = this._navCta(navEl);
          this._track('Academy CTA Clicked', Object.assign(
            { 'CTA Name': cta.name, 'CTA Type': cta.type, 'CTA Position': cta.position,
              'Target Screen Name': this._screenType(navEl.dataset.nav) },
            this._ctxOf(navEl)));
          this.navigate(navEl.dataset.nav);
        }
      });
    }

    _bindWatchEvents() {
      this.addEventListener('academy:lecture-watched', (e) => {
        const screen = e.target.closest('[data-screen^="screen-lecture-"]');
        if (!screen) return;
        this._completeLecture(screen.dataset.lectureId, screen.dataset.courseId, (e.detail && e.detail.percent) || 90, 'Video');
      });

      this.addEventListener('academy:lecture-played', (e) => {
        const screen = e.target.closest('[data-screen^="screen-lecture-"]');
        if (!screen) return;
        const lectureId = screen.dataset.lectureId;
        const courseId = screen.dataset.courseId;
        const levelId = this.levelOfCourse[courseId] || (this.courseMap[courseId] && this.courseMap[courseId]._levelId);
        const percent = (e.detail && e.detail.percent != null) ? e.detail.percent : 0;
        if (levelId && courseId && lectureId) {
          Api.watch(levelId, courseId, lectureId, percent)
            .then(() => this.refreshDashboard())
            .catch(() => {});
        }
      });
    }

    _levelHycash(levelId) {
      const ids = this.coursesOfLevel[levelId] || [];
      return ids.reduce((sum, cid) => sum + ((this.courseMap[cid] && this.courseMap[cid].hycashEarned) || 0), 0);
    }

    _pauseCurrentVideo() {
      const id = this._currentScreenId;
      if (!id || id.indexOf('screen-lecture-') !== 0) return;
      const scr = this.querySelector('[data-screen="' + id + '"]');
      const player = scr && scr.querySelector('academy-lecture-player');
      if (player && typeof player.pause === 'function') player.pause();
    }

    _screenDepth(id) {
      if (!id || id === 'home') return 0;
      if (id.indexOf('screen-level-') === 0) return 1;
      if (id.indexOf('screen-course-') === 0) return 2;
      if (id.indexOf('screen-lecture-') === 0) return 3;
      if (id.indexOf('screen-quiz-') === 0) return 3;
      return 1;
    }

    navigate(screenId) {
      if (!screenId) return;
      const target = this.querySelector('[data-screen="' + screenId + '"]');
      if (!target) return;

      if (screenId !== this._currentScreenId) this._pauseCurrentVideo();

      if (this._screenDepth(screenId) < this._screenDepth(this._currentScreenId)) {
        this._trackScreenExit(screenId);
      }

      const prevScreenId = this._currentScreenId;
      this.querySelectorAll('.academy-screen--active').forEach((s) => s.classList.remove('academy-screen--active'));
      target.classList.add('academy-screen--active');
      window.scrollTo({ top: 0, behavior: 'auto' });
      if (this.history[this.history.length - 1] !== screenId) this.history.push(screenId);

      this._currentScreenId = screenId;
      this._screenEnteredAt = Date.now();

      const ctx = this._ctxOf(target);
      this._track('Academy Screen Viewed', Object.assign({
        'Previous Screen Name': this._screenType(prevScreenId),
      }, ctx));

      this._trackStarted(screenId, ctx);

      if (target.tagName === 'ACADEMY-QUIZ' && typeof target.start === 'function') target.start();
    }

    _trackStarted(screenId, ctx) {
      if (screenId.indexOf('screen-level-') === 0) {
        const id = screenId.replace('screen-level-', '');
        if (!this._started.level.has(id)) { this._started.level.add(id); this._track('Academy Level Started', ctx); }
      } else if (screenId.indexOf('screen-course-') === 0) {
        const id = screenId.replace('screen-course-', '');
        if (!this._started.course.has(id)) { this._started.course.add(id); this._track('Academy Course Started', ctx); }
      } else if (screenId.indexOf('screen-lecture-') === 0) {
        const id = screenId.replace('screen-lecture-', '');
        if (!this._started.lecture.has(id)) { this._started.lecture.add(id); this._track('Academy Lecture Started', ctx); }
      }
    }

    _trackScreenExit(nextScreenId) {
      const from = this._currentScreenId;
      if (!from || from === nextScreenId) return;
      const secs = Math.round((Date.now() - (this._screenEnteredAt || Date.now())) / 1000);
      if (secs <= 0) return;
      const fromEl = this.querySelector('[data-screen="' + from + '"]');
      this._track('Academy Screen Exited', Object.assign({
        'Time Spent (s)': secs,
      }, fromEl ? this._ctxOf(fromEl) : { 'Screen Name': this._screenType(from) }));
    }

    async _completeLecture(lectureId, courseId, percent, source) {
      if (this.isLectureCompleted(lectureId)) return;
      const levelId = this.levelOfCourse[courseId] || (this.courseMap[courseId] && this.courseMap[courseId]._levelId);
      try {
        const res = await Api.watch(levelId, courseId, lectureId, percent || 90);
        await this.refreshDashboard();
        const onLectureScreen = this._currentScreenId === ('screen-lecture-' + lectureId);
        this._track('Academy Lecture Completed', Object.assign({
          'Watch Percentage': Math.round(percent || 90),
          'Completion Source': source || 'Button',
          'Time to Complete (s)': onLectureScreen ? Math.round((Date.now() - (this._screenEnteredAt || Date.now())) / 1000) : undefined,
        }, this._hier({ lectureId: lectureId, courseId: courseId, levelId: levelId })));
        if (res && res.allLecturesDone) {
          this._track('Academy Course Quiz Unlocked', this._hier({ courseId: courseId, levelId: levelId }));
          this.toast.show('All lectures done — course quiz unlocked! ✏️');
        } else this.toast.show('Lecture marked as watched ✓');
      } catch (e) {
        this.toast.show('Could not save your progress. Please try again.');
      }
    }

    _wireResultModal() {
      if (!this.resultModal) return;
      const primary = this.resultModal.querySelector('[data-result-primary]');
      const secondary = this.resultModal.querySelector('[data-result-secondary]');
      if (primary) primary.onclick = () => {
        this.resultModal.hidden = true;
        const cid = this._resultCourseId;
        const levelId = this._resultLevelId;
        this._track('Academy CTA Clicked', Object.assign(
          { 'CTA Name': this._label(primary), 'CTA Type': 'Quiz Result', 'Screen Name': 'Quiz Result' },
          this._hier({ courseId: cid, levelId: levelId })));

        const next = cid ? this._nextCourseId(cid) : null;
        if (next && this.isCourseUnlocked(next)) {
          const nLecs = this.lecturesOfCourse[next] || [];
          if (nLecs.length) this.navigate('screen-lecture-' + nLecs[0]);
          else this.navigate('screen-course-' + next);
        } else if (next) {
          const when = this._fmtWhen((this.courseMap[next] && this.courseMap[next].unlocksAt) || (cid && this.courseMap[cid] && this.courseMap[cid].nextCourseUnlocksAt));
          this.toast.show(when ? ('The next course unlocks on ' + when + '.') : 'The next course is locked for now.');
          if (levelId) this.navigate('screen-level-' + levelId);
          else if (cid) this.navigate('screen-course-' + cid);
          else this.navigate('home');
        } else if (levelId) {
          this.navigate('screen-level-' + levelId);
        } else if (cid) {
          this.navigate('screen-course-' + cid);
        } else {
          this.navigate('home');
        }
      };
      if (secondary) secondary.onclick = () => {
        this.resultModal.hidden = true;
        this._track('Academy CTA Clicked', Object.assign(
          { 'CTA Name': this._label(secondary), 'CTA Type': 'Quiz Result', 'Screen Name': 'Quiz Result' },
          this._hier({ courseId: this._resultCourseId })));
        if (this._resultCourseId) this.navigate('screen-quiz-' + this._resultCourseId);
      };
    }

    showResult(result, courseId) {
      if (!this.resultModal || !result) return;
      this._resultCourseId = courseId;
      this._resultLevelId = this.levelOfCourse[courseId] || (this.courseMap[courseId] && this.courseMap[courseId]._levelId);
      const set = (sel, val) => { const el = this.resultModal.querySelector(sel); if (el) el.textContent = val; };
      const stars = Math.max(0, Math.min(3, result.stars || 0));
      const gradeMap = { outstanding: 'Outstanding!', great: 'Great job!', good: 'Good effort!', practice: 'Keep practising' };
      set('[data-result-score]', result.correctCount != null ? result.correctCount : 0);
      set('[data-result-total]', result.totalQuestions != null ? result.totalQuestions : 0);
      set('[data-result-correct]', (result.correctCount || 0) + ' of ' + (result.totalQuestions || 0) + ' correct');
      set('[data-result-stars]', '⭐'.repeat(stars) || '—');
      set('[data-result-grade]', gradeMap[result.grade] || (result.grade || 'Result'));
      const icon = this.resultModal.querySelector('[data-result-icon]');
      if (icon) icon.textContent = stars >= 3 ? '🏆' : (stars === 2 ? '🎉' : '📚');
      const msg = this.resultModal.querySelector('[data-result-message]');
      if (msg) {
        msg.textContent = result.hycashEarned > 0
          ? 'You have earned ' + result.hycashEarned + ' HYCash - It’ll be credited to your wallet when you complete the whole level'
          : 'Course complete! Keep going to earn HYCash.';
      }
      this.resultModal.hidden = false;
    }

    _wireCertModal() {
      if (!this.certModal) return;
      const close = this.certModal.querySelector('[data-cert-close]');
      const dl = this.certModal.querySelector('[data-cert-download]');
      const share = this.certModal.querySelector('[data-cert-share]');
      if (close) close.onclick = () => { this.certModal.hidden = true; };
      this.certModal.addEventListener('click', (e) => { if (e.target === this.certModal) this.certModal.hidden = true; });
      if (dl) dl.onclick = () => this._downloadCert();
      if (share) share.onclick = () => this._shareCert();
    }

    _openCert(info) {
      if (!this.certModal) return;
      this._certInfo = info;
      this._ensureCertFonts();
      const set = (sel, val) => { const el = this.certModal.querySelector(sel); if (el) el.textContent = val; };
      set('[data-cert-ico]', info.final ? '🎓' : '📜');
      set('[data-cert-title]', info.final ? 'Graduation Certificate' : 'Certificate of Completion');
      const nameEl = this.certModal.querySelector('[data-cert-name]');
      if (nameEl) {
        nameEl.textContent = (info.name && info.name.trim()) || '';
        nameEl.setAttribute('contenteditable', 'true');
        nameEl.setAttribute('spellcheck', 'false');
        nameEl.oninput = () => this._saveCertName(nameEl.textContent.replace(/\s+/g, ' ').trim());
        nameEl.onkeydown = (ev) => { if (ev.key === 'Enter') { ev.preventDefault(); nameEl.blur(); } };
      }
      set('[data-cert-level]', info.title || '');
      set('[data-cert-sub]', info.sub || '');
      const hc = this.certModal.querySelector('[data-cert-hc]');
      if (hc) {
        if (info.hycash > 0) { hc.hidden = false; hc.textContent = '🏆 +' + info.hycash + ' HYCash'; }
        else hc.hidden = true;
      }
      const dl = this.certModal.querySelector('[data-cert-download]');
      const share = this.certModal.querySelector('[data-cert-share]');
      if (dl) dl.disabled = false;
      if (share) share.disabled = false;
      this.certModal.hidden = false;
      this._track('Academy Certificate Opened', {
        'Is Final': !!info.final,
        'Level ID': info.levelId,
        'Level Name': info.title,
        'HYcash': info.hycash,
      });
    }

    _ensureCertFonts() {
      if (this._certFontsPromise) return this._certFontsPromise;
      this._certFontsPromise = (async () => {
        try {
          const selfUrl = this.dataset.certFont;
          if (selfUrl && window.FontFace && document.fonts) {
            const face = new FontFace('Great Vibes', 'url(' + selfUrl + ')');
            await face.load();
            document.fonts.add(face);
            if (document.fonts.check('400 64px "Great Vibes"')) return;
          }
        } catch (e) { /* fall through to the Google method below */ }

        try {
          if (window.FontFace && document.fonts) {
            const css = await fetch('https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap')
              .then((r) => r.text());
            const faces = [];
            css.split('@font-face').slice(1).forEach((block) => {
              const um = block.match(/url\((https:[^)]+\.(?:woff2|woff|ttf))\)/);
              const rm = block.match(/unicode-range:\s*([^;]+);/i);
              if (um) faces.push({ url: um[1], range: rm ? rm[1].trim() : null });
            });
            if (faces.length) {
              await Promise.all(faces.map((f) => {
                const desc = f.range ? { unicodeRange: f.range } : {};
                const face = new FontFace('Great Vibes', 'url(' + f.url + ')', desc);
                return face.load().then((ff) => document.fonts.add(ff)).catch(() => {});
              }));
              if (document.fonts.check('400 64px "Great Vibes"')) return;
            }
          }
        } catch (e) { /* fall through to the <link> method below */ }

        try {
          if (!document.getElementById('ha-cert-font')) {
            const link = document.createElement('link');
            link.id = 'ha-cert-font';
            link.rel = 'stylesheet';
            link.href = 'https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap';
            document.head.appendChild(link);
          }
          if (document.fonts && document.fonts.load && document.fonts.check) {
            const spec = '400 64px "Great Vibes"';
            const deadline = Date.now() + 3000;
            while (Date.now() < deadline) {
              try { await document.fonts.load(spec); } catch (e) {}
              if (document.fonts.check(spec)) break;
              await new Promise((r) => setTimeout(r, 100));
            }
          } else {
            await new Promise((r) => setTimeout(r, 800));
          }
        } catch (e) { /* fall back to the system cursive stack */ }
      })();
      return this._certFontsPromise;
    }

    _loadCertTemplate(url) {
      if (!url) return Promise.resolve(null);
      if (this._tplCache && this._tplCache.url === url) return this._tplCache.p;
      const p = new Promise((res) => {
        const im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = () => res(im);
        im.onerror = () => res(null);
        im.src = url;
      });
      this._tplCache = { url, p };
      return p;
    }

    async _certCanvas(info) {
      info = info || {};
      const url = info.template || this.dataset.certTemplate || '';
      const tpl = await this._loadCertTemplate(url);
      const c = document.createElement('canvas');
      if (tpl) {
        const W = tpl.naturalWidth || tpl.width, H = tpl.naturalHeight || tpl.height;
        c.width = W; c.height = H;
        const x = c.getContext('2d');
        x.drawImage(tpl, 0, 0, W, H);
        this._drawTemplateText(x, W, H, info);
      } else {
        const W = 1397, H = 1126; c.width = W; c.height = H;
        this._drawFallback(c.getContext('2d'), W, H, info);
      }
      return c;
    }

    _certScript(px) {
      return 'italic 400 ' + px + 'px "Great Vibes", "Snell Roundhand", "Brush Script MT", "Segoe Script", cursive';
    }
    _certDesc(info) {
      return (info.desc && info.desc.trim()) ||
        (info.final
          ? 'Awarded for completing every level, course, lecture and assessment in the HYPHEN skincare curriculum.'
          : 'A complete journey through the essentials of skin science, ingredients and daily care routines.');
    }
    _wrapText(x, text, maxW) {
      const words = (text || '').split(' '); const lines = []; let ln = '';
      words.forEach((w) => {
        const t = ln ? ln + ' ' + w : w;
        if (x.measureText(t).width > maxW && ln) { lines.push(ln); ln = w; } else ln = t;
      });
      if (ln) lines.push(ln);
      return lines;
    }

    _drawTemplateText(x, W, H, info) {
      const cx = Math.round(W * 0.558), u = H / 1126;
      const SLATE = '#3B4651', GREEN = '#7AA03C', GREY = '#6E767C';
      const sp = (v) => { try { x.letterSpacing = (v || 0) + 'px'; } catch (e) {} };
      const ct = (t, yy, font, color, ls, ax) => { x.font = font; x.fillStyle = color; x.textAlign = 'center'; sp(ls); x.fillText(t, ax || cx, yy); sp(0); };

      ct(info.name || 'Hyphenator', 498 * u, this._certScript(66 * u), SLATE, 0);
      ct((info.title || 'Skincare Course'), 675 * u, '700 ' + (42 * u) + 'px Georgia, serif', GREEN, 1 * u);
      x.font = '400 ' + (21 * u) + 'px Georgia, serif'; x.fillStyle = GREY; x.textAlign = 'center'; sp(0);
      this._wrapText(x, this._certDesc(info), 720 * u).slice(0, 2)
        .forEach((l, i) => x.fillText(l, cx, (725 + i * 34) * u));
      ct(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
         920 * u, '400 ' + (28 * u) + 'px Georgia, serif', SLATE, 0, Math.round(W * 0.838));
    }

    _drawFallback(x, W, H, info) {
      const cx = W / 2, u = H / 1126;
      const BG = '#FAFAF6', SLATE = '#3B4651', GREEN = '#7AA03C', SAGE = '#A9BE73', GREY = '#6E767C';
      const sp = (v) => { try { x.letterSpacing = (v || 0) + 'px'; } catch (e) {} };
      const ct = (t, yy, font, color, ls) => { x.font = font; x.fillStyle = color; x.textAlign = 'center'; sp(ls); x.fillText(t, cx, yy); sp(0); };
      x.fillStyle = BG; x.fillRect(0, 0, W, H);
      x.strokeStyle = SLATE; x.lineWidth = 2.5 * u; x.strokeRect(46 * u, 46 * u, W - 92 * u, H - 92 * u);
      x.strokeStyle = SAGE; x.lineWidth = 1 * u; x.strokeRect(60 * u, 60 * u, W - 120 * u, H - 120 * u);
      ct('CERTIFICATE', 210 * u, '700 ' + (92 * u) + 'px Georgia, "Times New Roman", serif', SLATE, 12 * u);
      ct(info.final ? 'OF GRADUATION' : 'OF COMPLETION', 270 * u, '600 ' + (28 * u) + 'px Georgia, serif', GREEN, 8 * u);
      ct('THIS IS TO CERTIFY THAT', 382 * u, '600 ' + (22 * u) + 'px Georgia, serif', GREY, 4 * u);
      const name = info.name || 'Hyphenator';
      ct(name, 486 * u, this._certScript(72 * u), SLATE, 0);
      x.font = this._certScript(72 * u);
      const nw = Math.min(Math.max(x.measureText(name).width + 90 * u, 360 * u), 720 * u);
      x.strokeStyle = SAGE; x.lineWidth = Math.max(1, 1.5 * u);
      x.beginPath(); x.moveTo(cx - nw / 2, 520 * u); x.lineTo(cx + nw / 2, 520 * u); x.stroke();
      ct('has successfully completed the', 582 * u, 'italic 400 ' + (24 * u) + 'px Georgia, serif', GREY, 0);
      ct((info.title || 'Skincare Course'), 648 * u, '700 ' + (42 * u) + 'px Georgia, serif', GREEN, 1 * u);
      x.font = '400 ' + (21 * u) + 'px Georgia, serif'; x.fillStyle = GREY; x.textAlign = 'center'; sp(0);
      this._wrapText(x, this._certDesc(info), 760 * u).slice(0, 2)
        .forEach((l, i) => x.fillText(l, cx, (702 + i * 34) * u));
      ct('Issued on ' + new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
         900 * u, 'italic 400 ' + (24 * u) + 'px Georgia, serif', GREY, 0);
    }

    _readCertName() {
      const el = this.certModal && this.certModal.querySelector('[data-cert-name]');
      const v = el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
      if (v) this._saveCertName(v);
      return v;
    }

    async _downloadCert() {
      const info = this._certInfo || {};
      const typed = this._readCertName();
      if (typed) info.name = typed;
      try {
        await this._ensureCertFonts();
        const canvas = await this._certCanvas(info);
        const url = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = url; a.download = 'hyphen-academy-certificate.png';
        document.body.appendChild(a); a.click(); a.remove();
        this._track('Academy Certificate Downloaded', {
          'Is Final': !!info.final, 'Level ID': info.levelId, 'Level Name': info.title,
        });
      } catch (e) {
        this._track('Academy Certificate Download Failed', {
          'Is Final': !!info.final, 'Level ID': info.levelId, 'Level Name': info.title,
        });
        this.toast.show('Could not generate the certificate. Make sure the template is uploaded to Shopify Files.');
      }
    }

    async _shareCert() {
      await this._ensureCertFonts();
      const info = this._certInfo || {};
      const typed = this._readCertName();
      if (typed) info.name = typed;
      this._track('Academy Certificate Shared', {
        'Is Final': !!info.final, 'Level ID': info.levelId, 'Level Name': info.title,
      });
      const text = 'I completed ' + (info.title || 'a level') + ' at HYPHEN Academy! 🎓';
      const fallback = () => {
        if (navigator.share) { navigator.share({ text }).catch(() => {}); return; }
        if (navigator.clipboard) { navigator.clipboard.writeText(text + ' ' + window.location.href).then(() => this.toast.show('Copied to clipboard!')).catch(() => {}); return; }
        this._downloadCert();
      };
      try {
        const canvas = await this._certCanvas(info);
        canvas.toBlob(async (blob) => {
          if (!blob) { fallback(); return; }
          const file = new File([blob], 'hyphen-academy-certificate.png', { type: 'image/png' });
          try {
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
              await navigator.share({ files: [file], text });
              return;
            }
          } catch (e) { /* fall through */ }
          fallback();
        }, 'image/png');
      } catch (e) { fallback(); }
    }
  }

  customElements.define('academy-toast', AcademyToast);
  customElements.define('academy-lecture-player', AcademyLecturePlayer);
  customElements.define('academy-quiz', AcademyQuiz);
  customElements.define('academy-app', AcademyApp);
})();
